import { IrisDMSFolderI, IrisDMSFolder, IrisDmsRecentFilesFolder, IrisDmsRootFolderId } from '../models/IrisDMSFolder';
import { Dictionary } from '@ngrx/entity';
import flow from 'lodash/fp/flow';
import _flatten from 'lodash/fp/flatten';
import map from 'lodash/fp/map';
import { EntityMap } from '@ngrx/signals/entities';

export function mapFoldersToLine(folders: IrisDMSFolderI[], foldersMap: Dictionary<IrisDMSFolderI> = {}): IrisDMSFolderI[] {
  return flow(
    map((folder: IrisDMSFolderI) => flatFolder(folder, foldersMap)),
    _flatten,
  )(folders);
}

// Function explanation: folder.children => [folder, [children]]
export function flatFolder(folder: IrisDMSFolderI, foldersMap: Dictionary<IrisDMSFolderI> = {}): IrisDMSFolderI[] {
  if (!folder) {
    return [];
  }
  const existingFolder = new IrisDMSFolder(foldersMap[folder.id]);
  const updatedFolder = new IrisDMSFolder(Object.assign({}, { expanded: existingFolder.expanded }, folder));
  if (!folder.children) {
    return [updatedFolder];
  }
  return _flatten([
    updatedFolder,
    ...updatedFolder.children.map(_folder => flatFolder(_folder, foldersMap)),
  ]);
}

export function expandTree(folders: IrisDMSFolderI[], folderId: string): IrisDMSFolderI[] {
  const folder = folders.find(_folder => _folder.id === folderId);
  if (!folder) { return folders; }
  folder.expanded = true;
  return expandTree(folders, folder.parentId);
}

export function isFolderParentOfTheOtherByGeneralParent(
  generalParent: IrisDMSFolderI,
  probableParent: IrisDMSFolderI,
  probableChild: IrisDMSFolderI,
): boolean {
  const folderToExploreAsParent = flatFolder(generalParent)
    .find((child) => child.id === probableParent.id);

  if (folderToExploreAsParent?.id === probableChild.parentId) { return true; }
  const folderOneLevelAbove = flatFolder(folderToExploreAsParent)
    .find((child) => child.id === probableChild.parentId);

  if (!folderOneLevelAbove) { return false; }
  return isFolderParentOfTheOtherByGeneralParent(
    generalParent,
    folderOneLevelAbove,
    probableChild,
  );
}

export function getCorrectSelectedFolderIdAfterRemoveSuccess(
  selectedFolder: IrisDMSFolderI,
  removedFolder: IrisDMSFolderI,
  parentFolderOfSelected,
): string {
  if (!selectedFolder) { return null; }

  const isSelectedFolderRemoved = removedFolder.id === selectedFolder.id;
  const isSelectedFolderChildOfTheRemoved =
    isFolderParentOfTheOtherByGeneralParent(parentFolderOfSelected, removedFolder, selectedFolder);

  if (!isSelectedFolderRemoved && !isSelectedFolderChildOfTheRemoved) {
    return selectedFolder.id;
  }
  return IrisDmsRecentFilesFolder.id;
}

export function getFolderPathByFolderId(folderId: string, entities: EntityMap<IrisDMSFolderI>): IrisDMSFolderI[] {
  const folder = entities[folderId];

  const path = [];

  if (!folder || !folderId) {
    return path;
  }
  path.unshift(folder);
  if (folder.parentId !== IrisDmsRootFolderId && !!folder.parentId) {
    path.unshift(...getFolderPathByFolderId(folder.parentId, entities));
  }

  return path;
};
